import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { collection, getDocs } from "firebase/firestore";
import { withReadTimeout } from "../lib/firebase/readTimeout";
import { db } from "../lib/firebase/config";
import { useAuth } from "../hooks/useAuth";
import {
  blockUser as blockUserCallable,
  unblockUser as unblockUserCallable,
} from "../services/firebase/moderationService";
import { captureException } from "../lib/monitoring";

/**
 * BlocksContext — lista de usuários bloqueados pelo usuário logado.
 *
 * Exigência da política de UGC do Google Play: bloquear tem de esconder o
 * conteúdo do bloqueado. Na v1 o efeito é de LEITURA e roda no client — este
 * contexto é a fonte única dessa lista, e quem filtra (feed, resenhas) lê
 * daqui em vez de consultar o Firestore por conta própria.
 *
 * POR QUE `getDocs` E NÃO `onSnapshot`: a lista só muda por ação do próprio
 * usuário, dentro deste app, através das callables abaixo — então o estado
 * local é atualizado na hora e um listener permanente pagaria conexão para
 * observar algo que ninguém mais escreve. Custo: 1 leitura por sessão
 * (subcoleção pequena; a maioria das contas tem zero docs).
 *
 * FALHA ABERTA de propósito: se a leitura falhar, `blockedUids` fica vazio e
 * o conteúdo aparece. O oposto — esconder tudo quando não se sabe — deixaria
 * o feed em branco por um erro de rede, que é dano maior e mais confuso do
 * que ver por mais um instante alguém que você bloqueou.
 */
interface BlocksContextData {
  blockedUids: ReadonlySet<string>;
  /**
   * Uids bloqueados EM ALGUM MOMENTO desta sessão. Cresce e nunca encolhe —
   * inclusive no desbloqueio, e é justamente aí que ele serve.
   *
   * Bloquear apaga as duas arestas de follow no servidor
   * (`functions/src/moderation/blockUser.ts`) e desbloquear NÃO as restaura.
   * Sem este conjunto, quem bloqueia e desbloqueia sem sair da tela volta a
   * ver "Seguindo" num follow que já não existe — o `isFollowing` do perfil
   * foi lido na montagem e ninguém o corrige. Como o conjunto só cresce, a
   * derivação continua verdadeira depois do desbloqueio.
   */
  followSeveredUids: ReadonlySet<string>;
  isLoading: boolean;
  isBlocked: (uid: string | null | undefined) => boolean;
  /** true quando um bloqueio desta sessão desfez o follow com este uid. */
  isFollowSevered: (uid: string | null | undefined) => boolean;
  /**
   * Tira o uid do conjunto acima. Quem recriou a aresta chama isto — sem ele,
   * desbloquear e seguir de novo na mesma tela deixaria o botão preso em
   * "Seguir" para sempre, porque a marca de "foi cortado" nunca sairia.
   */
  markFollowRestored: (uid: string) => void;
  block: (uid: string) => Promise<void>;
  unblock: (uid: string) => Promise<void>;
}

const BlocksContext = createContext<BlocksContextData | null>(null);

export function BlocksProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [blockedUids, setBlockedUids] = useState<ReadonlySet<string>>(() => new Set());
  const [followSeveredUids, setFollowSeveredUids] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);

  const uid = user?.uid;

  useEffect(() => {
    if (!uid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset sincrono pre-subscription (mesmo padrao de ListsContext/UserBooksContext)
      setBlockedUids(new Set());
      // Idem: a sessão acabou, o rastro dela também. (A regra só acusa a
      // primeira chamada do bloco, então este setter não precisa de `disable`
      // próprio — um a mais viraria diretiva inútil, que o lint também acusa.)
      setFollowSeveredUids(new Set());
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    withReadTimeout(getDocs(collection(db, "users", uid, "blocks")))
      .then((snap) => {
        if (cancelled) return;
        setBlockedUids(new Set(snap.docs.map((d) => d.id)));
      })
      .catch((err) => {
        if (cancelled) return;
        // Falha aberta: mantém o set vazio. Ver o cabeçalho do arquivo.
        captureException(err, { context: "BlocksContext.load" });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid]);

  const block = useCallback(async (target: string) => {
    await blockUserCallable(target);
    setBlockedUids((prev) => new Set(prev).add(target));
    // Marca independentemente dos flags `unfollowed`/`removedFollower` da
    // resposta: depois de um bloqueio bem-sucedido não existe follow com este
    // uid — tendo existido antes ou não —, e é essa a afirmação que a UI
    // precisa. Ler os flags aqui só reintroduziria o `undefined` da callable
    // antiga como um terceiro estado.
    setFollowSeveredUids((prev) => new Set(prev).add(target));
  }, []);

  const unblock = useCallback(async (target: string) => {
    await unblockUserCallable(target);
    setBlockedUids((prev) => {
      const next = new Set(prev);
      next.delete(target);
      return next;
    });
  }, []);

  const isBlocked = useCallback(
    (candidate: string | null | undefined) => !!candidate && blockedUids.has(candidate),
    [blockedUids],
  );

  const isFollowSevered = useCallback(
    (candidate: string | null | undefined) =>
      !!candidate && followSeveredUids.has(candidate),
    [followSeveredUids],
  );

  const markFollowRestored = useCallback((target: string) => {
    setFollowSeveredUids((prev) => {
      if (!prev.has(target)) return prev;
      const next = new Set(prev);
      next.delete(target);
      return next;
    });
  }, []);

  const value = useMemo<BlocksContextData>(
    () => ({
      blockedUids,
      followSeveredUids,
      isLoading,
      isBlocked,
      isFollowSevered,
      markFollowRestored,
      block,
      unblock,
    }),
    [
      blockedUids,
      followSeveredUids,
      isLoading,
      isBlocked,
      isFollowSevered,
      markFollowRestored,
      block,
      unblock,
    ],
  );

  return <BlocksContext.Provider value={value}>{children}</BlocksContext.Provider>;
}

export function useBlocks(): BlocksContextData {
  const ctx = useContext(BlocksContext);
  if (!ctx) throw new Error("useBlocks requer BlocksProvider como ancestral");
  return ctx;
}

/**
 * Versão tolerante: `null` fora do provedor (harness, teste de tela isolada).
 * Quem filtra pessoas por bloqueio no client usa isto para não quebrar a tela —
 * o servidor confere o bloqueio de novo em todo caminho que importa.
 */
export function useOptionalBlocks(): BlocksContextData | null {
  return useContext(BlocksContext);
}
